import { Perspective } from '@feature/event-detail/types';

export interface BadgeInfo {
  icon: 'play' | 'photo';
  label: string;
}

export interface PerspectiveState {
  isRecordedPost: boolean;
  isLive: boolean;
  isEnded: boolean;
  hasVod: boolean;
  isProcessing: boolean;
}

/**
 * Derives the computed state for a perspective.
 */
export function getPerspectiveState(perspective: Partial<Perspective>): PerspectiveState {
  const isRecordedPost = perspective.postType === 'recorded';
  const isLive = perspective.streamStatus === 'live';
  const isEnded = perspective.streamStatus === 'ended';
  const hasVod = Boolean(perspective.m3u8Url);
  // thumbnailUrl means content is ready (set by thumbnail_added realtime event)
  const hasMedia = Boolean(perspective.videoUrl || perspective.photoUrl || perspective.m3u8Url || perspective.thumbnailUrl);

  // Processing logic differs by type:
  // - Recorded posts: pending OR (no processingState AND no media/thumbnail - i.e. placeholder)
  //   BUT if m3u8Url is set (vod_ready received), video is definitely ready
  // - Livestreams: ended but no VOD yet means still processing
  const isProcessing = isRecordedPost
    ? !hasVod && (perspective.processingState === 'pending' || (!perspective.processingState && !hasMedia))
    : isEnded && !hasVod;

  return {
    isRecordedPost,
    isLive,
    isEnded,
    hasVod,
    isProcessing,
  };
}

/**
 * Determines what badge to display for a perspective.
 * Returns null if no badge should be shown.
 *
 * Note: LIVE badge is handled separately (rendered when streamStatus === 'live')
 */
export function getBadgeInfo(perspective: Partial<Perspective>): BadgeInfo | null {
  const { isRecordedPost, hasVod, isProcessing } = getPerspectiveState(perspective);

  // Livestreams: only show VOD badge (LIVE badge handled separately)
  if (perspective.postType === 'livestream') {
    if (hasVod) {
      return { icon: 'play', label: 'VOD' };
    }
    return null;
  }

  // Recorded content: show VIDEO or PHOTO badge when ready (not processing)
  if (perspective.postType === 'recorded' && !isProcessing) {
    if (perspective.videoUrl || perspective.m3u8Url) {
      return { icon: 'play', label: 'VIDEO' };
    }
    // photoUrl OR thumbnailUrl without video = photo content
    if (perspective.photoUrl || (perspective.thumbnailUrl && !perspective.videoUrl)) {
      return { icon: 'photo', label: 'PHOTO' };
    }
  }

  return null;
}

/**
 * Determines if the play icon overlay should be shown.
 */
export function shouldShowPlayIcon(perspective: Partial<Perspective>): boolean {
  const { hasVod, isRecordedPost } = getPerspectiveState(perspective);
  return hasVod || (isRecordedPost && Boolean(perspective.videoUrl));
}
